import axios from "axios";
import * as Sentry from "@sentry/react";
import type { PluginListenerHandle } from "@capacitor/core";
import { AssignmentPlatform } from "refsix-core";
import type { ScrapePayload } from "refsix-core";
import request from "../request";
import { scheduleBrowser, type ScheduleBrowser } from "./scheduleBrowser";
import {
    isPlatformUrl,
    parseScrapeMessage,
    scrapeDisposition,
    isScheduleUrl,
    shouldRedirectToSchedule,
    type ScrapeDisposition,
    type ScrapeMessage,
} from "./inAppScrapeMessages";
import { reportScrapeFailure } from "./scrapeFailureReport";

/**
 * Importing appointments in the app, without the browser extension (REFSIX-568).
 *
 * The extension exists because Arbiter Sports and MOAS have no public API, so
 * the only place a referee's schedule can be read is a browser holding their
 * session. An in-app WebView is such a browser: it has the same three
 * primitives an extension gets — a page with the user's cookies, a way to run
 * our script in it, and a channel back — so the same scrape works on the phone,
 * which is where referees actually are.
 *
 * The flow:
 *
 *   1. open the platform's schedule page in the WebView, and let the user sign
 *      in there, on the platform's own login form;
 *   2. inject the scraper on every page load of the platform's site (see
 *      scheduleBrowser.ts for which plugin does that, per platform). Until the
 *      user reaches their schedule it reports "not a schedule page" and we
 *      keep waiting;
 *   3. when it reads a schedule, close the WebView and POST the fixtures to
 *      refauth's existing `/integrations/assignments/matches` — the same
 *      endpoint, payload and validation the extension uses;
 *   4. report what refauth did with them.
 *
 * The user's platform credentials never reach app code, let alone refauth: they
 * are typed into the platform's page inside the WebView, and only parsed
 * fixtures cross back.
 *
 * Session persistence is the WebView's own cookie jar, so a second import
 * usually needs no sign-in. It is not shared with Safari or Chrome, so the first
 * one always does.
 */

const IMPORT_PATH = "/integrations/assignments/matches";

/**
 * How long to leave the WebView open with nothing happening.
 *
 * Generous because the user is signing in inside it, possibly fetching a
 * password from elsewhere on the phone — which backgrounds the app. It is a
 * backstop against a leaked listener, not a UX timer: the user can close the
 * WebView whenever they like, and that resolves the import as cancelled.
 */
const OVERALL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * The trail of an import, attached to whatever is reported afterwards. An import
 * is a conversation with a site we do not control, so when one goes wrong the
 * only useful evidence is which of its pages the user reached.
 */
function breadcrumb(message: string): void {
    Sentry.addBreadcrumb({ category: "assignments", message });
}

export interface ImportCounts {
    added: number;
    updated: number;
    unchanged: number;
    skipped: number;
}

export type InAppImportOutcome =
    | ({ status: "imported"; platform: string } & ImportCounts)
    /** The user closed the WebView before a schedule was read. */
    | { status: "cancelled" }
    /** A schedule page we could not turn into an import. */
    | {
          status: "notImported";
          reason: "noFixtures" | "allInPast" | "tooMany" | "error";
          found?: number;
          diagnostics?: string;
      }
    /** The scrape worked; sending it to REFSIX did not. */
    | { status: "importFailed"; sessionExpired?: boolean }
    /** Nothing happened for OVERALL_TIMEOUT_MS. */
    | { status: "timedOut" };

/**
 * refauth answers with the documents it touched. The app only shows counts, so
 * the arrays are reduced here rather than carried around.
 */
interface ImportResponse {
    added?: unknown[];
    updated?: unknown[];
    unchanged?: unknown[];
    invalid?: unknown[];
}

export function summariseImportResponse(
    body: ImportResponse | undefined
): ImportCounts {
    const size = (value: unknown[] | undefined) =>
        Array.isArray(value) ? value.length : 0;

    return {
        added: size(body?.added),
        updated: size(body?.updated),
        unchanged: size(body?.unchanged),
        // refauth calls these "invalid": rows it could not turn into a match.
        // "Skipped" is what the user needs to hear — the import as a whole
        // succeeded, and these rows were left out of it.
        skipped: size(body?.invalid),
    };
}

export async function importAssignmentsInApp(
    platform: AssignmentPlatform
): Promise<InAppImportOutcome> {
    const browser = await scheduleBrowser();

    const read = await readScheduleInWebView(platform, browser);
    if (read.status !== "read") {
        return read.outcome;
    }

    return sendToRefsix(read.payload);
}

type ScheduleRead =
    | { status: "read"; payload: ScrapePayload }
    | { status: "gaveUp"; outcome: InAppImportOutcome };

/**
 * Opens the WebView and resolves once the scraper has read a schedule out of it,
 * or the attempt ended some other way.
 *
 * Deliberately one function: the listeners, the timeout and the WebView are a
 * single lifetime, and splitting them up is how you leak one of them.
 */
async function readScheduleInWebView(
    platform: AssignmentPlatform,
    browser: ScheduleBrowser
): Promise<ScheduleRead> {
    const listeners: PluginListenerHandle[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    // At most one automatic navigation per import — see shouldRedirectToSchedule.
    let sentToSchedule = false;
    let lastWaiting: ScrapeMessage | undefined;
    let pagesSeen = 0;

    return new Promise<ScheduleRead>((resolve) => {
        const finish = (read: ScheduleRead, closeWebView: boolean) => {
            if (settled) {
                return;
            }
            settled = true;

            // The user gave up (or we timed out) on the schedule page itself
            // with nothing claiming it: the likeliest sign of a markup change.
            if (
                read.status === "gaveUp" &&
                // oxlint-disable-next-line refsix/no-magic-enum-string -- InAppImportOutcome's status, not MatchStatusExternal
                (read.outcome.status === "cancelled" ||
                    read.outcome.status === "timedOut") &&
                lastWaiting &&
                isScheduleUrl(lastWaiting.url, platform.scheduleUrl)
            ) {
                reportScrapeFailure(platform, {
                    reason: "unrecognisedSchedulePage",
                    message: lastWaiting,
                    diagnostics: lastWaiting.result?.diagnostics,
                    redirectedToSchedule: sentToSchedule,
                    pagesSeen,
                });
            }

            if (timer) {
                clearTimeout(timer);
            }
            listeners.forEach((listener) => {
                listener
                    .remove()
                    .catch((err) =>
                        console.warn(
                            "Could not remove an in-app browser listener",
                            err
                        )
                    );
            });

            if (closeWebView) {
                browser
                    .close()
                    .catch((err) =>
                        console.warn("Could not close the in-app browser", err)
                    );
            }

            resolve(read);
        };

        const act = (
            disposition: ScrapeDisposition,
            message: ScrapeMessage
        ) => {
            const url = message.url;
            switch (disposition.act) {
                case "waiting":
                    lastWaiting = message;
                    // Every page on the way to the schedule lands here, the
                    // login page included. A breadcrumb rather than a warning
                    // because it is the normal case — but when an import does
                    // stall it is the only record of what the user was looking
                    // at when it did.
                    breadcrumb(
                        `not a ${platform.id} schedule page yet: ${url} ${
                            disposition.diagnostics ?? ""
                        }`
                    );

                    // Signed in, but on the platform's own pages rather than the
                    // schedule — Arbiter returns you to a dashboard that has no
                    // fixture table. Go and fetch the page we actually need.
                    if (
                        !sentToSchedule &&
                        shouldRedirectToSchedule(url, platform.scheduleUrl)
                    ) {
                        sentToSchedule = true;
                        breadcrumb(
                            `sending the WebView to the ${platform.id} schedule page`
                        );
                        browser
                            .goToSchedule(platform)
                            .catch((err) =>
                                console.warn(
                                    "Could not navigate the in-app browser to the schedule page",
                                    err
                                )
                            );
                    }
                    return;

                case "import": {
                    const payload = disposition.result.payload;

                    // The scrape names its own platform, from whichever adapter
                    // claimed the page. It should always be the one whose page we
                    // opened — but the import is keyed on this, and importing one
                    // platform's fixtures under another's id prefix would make
                    // duplicates nobody can merge.
                    if (payload.platform !== platform.id) {
                        console.warn(
                            `REFSIX: a ${payload.platform} scrape arrived on the ${platform.id} page`
                        );
                        return;
                    }

                    breadcrumb(
                        `read ${payload.fixtures.length} ${platform.id} ` +
                            `appointment(s) from ${disposition.result.pages} page(s)`
                    );
                    finish({ status: "read", payload }, true);
                    return;
                }

                case "failed":
                    // A schedule of nothing but past games read correctly.
                    if (disposition.reason !== "allInPast") {
                        reportScrapeFailure(platform, {
                            reason: disposition.reason,
                            message,
                            found: disposition.found,
                            diagnostics: disposition.diagnostics,
                            redirectedToSchedule: sentToSchedule,
                            pagesSeen,
                        });
                    }
                    finish(
                        {
                            status: "gaveUp",
                            outcome: {
                                status: "notImported",
                                reason: disposition.reason,
                                found: disposition.found,
                                diagnostics: disposition.diagnostics,
                            },
                        },
                        true
                    );
                    return;
            }
        };

        const start = async () => {
            listeners.push(
                ...(await browser.listen({
                    onMessage: (detail) => {
                        const message = parseScrapeMessage(detail);
                        if (!message) {
                            // Not ours. The page's own scripts can share this
                            // bridge (Android), or this is not a scrape at all.
                            return;
                        }

                        // The adapters recognise a schedule by its markup, not
                        // by its host — that is the extension manifest's job,
                        // and there is no manifest here. So the host check is
                        // ours: a page on some other site that happens to carry
                        // similar markup must not be imported as this
                        // platform's appointments. (The iOS plugin checks the
                        // sender's origin natively too.)
                        if (!isPlatformUrl(message.url, platform)) {
                            console.warn(
                                `REFSIX: ignoring a scrape from outside ${platform.id}`,
                                message.url
                            );
                            return;
                        }

                        pagesSeen += 1;
                        act(scrapeDisposition(message), message);
                    },
                    onClosed: () => {
                        // Already closing itself, so do not ask it to close again.
                        finish(
                            {
                                status: "gaveUp",
                                outcome: { status: "cancelled" },
                            },
                            false
                        );
                    },
                }))
            );

            timer = setTimeout(
                () =>
                    finish(
                        { status: "gaveUp", outcome: { status: "timedOut" } },
                        true
                    ),
                OVERALL_TIMEOUT_MS
            );

            await browser.open(platform);
        };

        start().catch((err) => {
            console.error("Could not open the assignment platform", err);
            finish(
                {
                    status: "gaveUp",
                    outcome: { status: "importFailed" },
                },
                true
            );
        });
    });
}

/**
 * Posts the scraped fixtures to refauth — the same endpoint and payload the
 * browser extension uses, so everything about turning a scraped fixture into a
 * REFSIX match stays in one place, server-side, where it can be fixed with a
 * deploy.
 */
async function sendToRefsix(
    payload: ScrapePayload
): Promise<InAppImportOutcome> {
    try {
        const response = await request.post<ImportResponse>(
            IMPORT_PATH,
            payload
        );

        return {
            status: "imported",
            platform: payload.platform,
            ...summariseImportResponse(response.data),
        };
    } catch (err) {
        const httpStatus = axios.isAxiosError(err)
            ? err.response?.status
            : undefined;
        console.error("Could not import scraped appointments", httpStatus, err);
        return {
            status: "importFailed",
            sessionExpired: httpStatus === 401,
        };
    }
}
